/**
 * Configuration for amenity taxonomy used in listing creation.
 * Defines all amenity categories and their associated amenities.
 */

/**
 * Amenity data structure:
 * - id: Unique identifier for the amenity
 * - name: Display name for the amenity
 * - icon: Icon identifier (to be mapped to actual icon components)
 * - hasOptions: Boolean indicating if amenity has additional options
 * - options: Array of option strings (only if hasOptions is true)
 * - requiresInput: Special input type identifier (e.g., 'wifi-speed')
 */

// Accessibility category amenities
const accessibilityAmenities = [
  { id: 'main_level_bathroom', name: 'Main level bathroom', icon: 'bathroom' },
  { id: 'main_level_bedroom', name: 'Main level bedroom', icon: 'bedroom' },
  { id: 'shower_chair', name: 'Shower chair', icon: 'chair' },
  { id: 'single_level_home', name: 'Single level home', icon: 'home' },
  { id: 'step_free_entrance', name: 'Step-free entrance', icon: 'entrance' },
];

// Bathroom category amenities
const bathroomAmenities = [
  { id: 'baby_bath', name: 'Baby bath', icon: 'baby' },
  { id: 'bathtub', name: 'Bathtub', icon: 'bathtub' },
  { id: 'cleaning_products', name: 'Cleaning products', icon: 'cleaning' },
  { id: 'hair_dryer', name: 'Hair dryer', icon: 'hairdryer' },
  { id: 'hot_water', name: 'Hot water', icon: 'water' },
  {
    id: 'starter_toiletries',
    name: 'Starter toiletries',
    icon: 'toiletries',
    hasOptions: true,
    options: ['shampoo', 'body wash', 'conditioner', 'hand soap', 'toilet paper'],
  },
  { id: 'towels', name: 'Towels', icon: 'towel' },
];

// Bedroom and laundry category amenities
const bedroomAndLaundryAmenities = [
  { id: 'bed_linens', name: 'Bed linens', icon: 'bed' },
  { id: 'blackout_shades', name: 'Blackout shades', icon: 'shades' },
  { id: 'clothesline', name: 'Clothesline', icon: 'clothesline' },
  { id: 'clothing_storage', name: 'Clothing storage', icon: 'storage' },
  { id: 'dryer', name: 'Dryer', icon: 'dryer' },
  { id: 'drying_rack', name: 'Drying rack', icon: 'rack' },
  { id: 'iron', name: 'Iron', icon: 'iron' },
  { id: 'king_bed', name: 'King bed', icon: 'bed' },
  { id: 'room_darkening_shades', name: 'Room-darkening shades', icon: 'shades' },
  {
    id: 'starter_essentials_bedroom',
    name: 'Starter essentials',
    icon: 'essentials',
    hasOptions: true,
    options: ['detergent', 'dryer sheets', 'softener'],
  },
  { id: 'steamer', name: 'Steamer', icon: 'steamer' },
  { id: 'washer', name: 'Washer', icon: 'washer' },
];

// Eco-friendly category amenities
const ecoFriendlyAmenities = [
  { id: 'compost_bin', name: 'Compost bin', icon: 'compost' },
  { id: 'ev_charger', name: 'EV charger', icon: 'ev' },
  { id: 'solar_panels', name: 'Solar panels', icon: 'solar' },
];

// Entertainment category amenities
const entertainmentAmenities = [
  { id: 'arcade_games', name: 'Arcade games', icon: 'arcade' },
  { id: 'books_and_reading_material', name: 'Books and reading material', icon: 'books' },
  {
    id: 'instruments',
    name: 'Instruments',
    icon: 'music',
    hasOptions: true,
    options: ['piano', 'guitar', 'other'],
  },
  { id: 'puzzles', name: 'Puzzles', icon: 'puzzle' },
  { id: 'record_player', name: 'Record player', icon: 'record' },
  { id: 'smart_tv', name: 'Smart TV', icon: 'tv' },
  { id: 'sound_system', name: 'Sound system', icon: 'sound' },
  { id: 'tv', name: 'TV', icon: 'tv' },
];

// Family category amenities
const familyAmenities = [
  { id: 'adult_bikes', name: 'Adult bikes', icon: 'bike' },
  { id: 'baby_gates', name: 'Baby gates', icon: 'gate' },
  { id: 'beach_toys', name: 'Beach toys', icon: 'beach' },
  { id: 'board_games', name: 'Board games', icon: 'games' },
  {
    id: 'car_seat',
    name: 'Car seat',
    icon: 'carseat',
    hasOptions: true,
    options: ['Infant', 'Booster', 'Convertible'],
  },
  { id: 'changing_table', name: 'Changing table', icon: 'table' },
  { id: 'crib', name: 'Crib', icon: 'crib' },
  { id: 'kids_bikes', name: "Kid's bikes", icon: 'bike' },
  {
    id: 'kids_books_and_toys',
    name: "Kid's books and toys",
    icon: 'toys',
    hasOptions: true,
    options: ['0–2 years old', '3–5 years old', '6–9 years old', '10-12 years old', '13+ years old'],
  },
  { id: 'outlet_covers', name: 'Outlet covers', icon: 'outlet' },
  { id: 'pack_n_play', name: "Pack 'n play/travel crib", icon: 'crib' },
  { id: 'stroller', name: 'Stroller', icon: 'stroller' },
];

// Heating and cooling category amenities
const heatingAndCoolingAmenities = [
  { id: 'air_conditioning', name: 'Air conditioning', icon: 'ac' },
  { id: 'ceiling_fans', name: 'Ceiling fans', icon: 'fan' },
  { id: 'heating', name: 'Heating', icon: 'heating' },
  { id: 'indoor_fireplace', name: 'Indoor fireplace', icon: 'fireplace' },
  { id: 'portable_fans', name: 'Portable fans', icon: 'fan' },
];

// Internet and office category amenities
const internetAndOfficeAmenities = [
  { id: 'dedicated_workspace', name: 'Dedicated workspace', icon: 'workspace' },
  { id: 'monitor', name: 'Monitor', icon: 'monitor' },
  { id: 'office', name: 'Office', icon: 'office' },
  { id: 'printer_scanner', name: 'Printer/scanner', icon: 'printer' },
  { id: 'wifi', name: 'Wifi', icon: 'wifi', requiresInput: 'wifi-speed' },
];

// Kitchen and dining category amenities
const kitchenAndDiningAmenities = [
  { id: 'basic_spices', name: 'Basic spices', icon: 'spices' },
  { id: 'blender', name: 'Blender', icon: 'blender' },
  { id: 'cleaning_products_kitchen', name: 'Cleaning products', icon: 'cleaning' },
  { id: 'coffee_maker', name: 'Coffee maker', icon: 'coffee' },
  {
    id: 'cooking_basics',
    name: 'Cooking basics',
    icon: 'cooking',
    hasOptions: true,
    options: ['pots and pans', 'oil', 'salt and pepper'],
  },
  { id: 'dining_table', name: 'Dining table', icon: 'table' },
  { id: 'dishes_and_silverware', name: 'Dishes and silverware', icon: 'dishes' },
  { id: 'dishwasher', name: 'Dishwasher', icon: 'dishwasher' },
  { id: 'freezer', name: 'Freezer', icon: 'freezer' },
  { id: 'high_chair', name: 'High chair', icon: 'chair' },
  { id: 'hot_water_kettle', name: 'Hot water kettle', icon: 'kettle' },
  { id: 'kids_dishes_and_utensils', name: "Kid's dishes and utensils", icon: 'dishes' },
  { id: 'kitchen', name: 'Kitchen', icon: 'kitchen' },
  { id: 'microwave', name: 'Microwave', icon: 'microwave' },
  { id: 'oven', name: 'Oven', icon: 'oven' },
  { id: 'premium_coffee_maker', name: 'Premium coffee maker', icon: 'coffee' },
  { id: 'refrigerator', name: 'Refrigerator', icon: 'fridge' },
  {
    id: 'starter_essentials_kitchen',
    name: 'Starter essentials',
    icon: 'essentials',
    hasOptions: true,
    options: ['paper towels', 'dish soap', 'dishwasher pods', 'sponge', 'hand soap'],
  },
  { id: 'stove', name: 'Stove', icon: 'stove' },
  { id: 'toaster', name: 'Toaster', icon: 'toaster' },
  { id: 'tupperware', name: 'Tupperware', icon: 'tupperware' },
  { id: 'wine_glasses', name: 'Wine glasses', icon: 'wine' },
];

// Outdoor category amenities
const outdoorAmenities = [
  { id: 'backyard', name: 'Backyard', icon: 'backyard' },
  { id: 'balcony', name: 'Balcony', icon: 'balcony' },
  { id: 'bbq', name: 'BBQ', icon: 'bbq' },
  { id: 'driveway', name: 'Driveway', icon: 'driveway' },
  { id: 'fire_pit', name: 'Fire pit', icon: 'firepit' },
  { id: 'free_parking', name: 'Free parking', icon: 'parking' },
  { id: 'garage', name: 'Garage', icon: 'garage' },
  { id: 'outdoor_dining_area', name: 'Outdoor dining area', icon: 'dining' },
  { id: 'outdoor_furniture', name: 'Outdoor furniture', icon: 'furniture' },
  { id: 'outdoor_playground', name: 'Outdoor playground', icon: 'playground' },
  { id: 'patio', name: 'Patio', icon: 'patio' },
];

// Safety and property category amenities
const safetyAndPropertyAmenities = [
  { id: 'carbon_monoxide_detector', name: 'Carbon monoxide detector', icon: 'detector' },
  { id: 'exterior_cameras', name: 'Exterior cameras', icon: 'camera' },
  { id: 'fire_extinguisher', name: 'Fire extinguisher', icon: 'extinguisher' },
  { id: 'fire_ladder', name: 'Fire ladder', icon: 'ladder' },
  { id: 'first_aid_kit', name: 'First-aid kit', icon: 'firstaid' },
  { id: 'humidifier_dehumidifier', name: 'Humidifier / Dehumidifier', icon: 'humidifier' },
  { id: 'keypad', name: 'Keypad', icon: 'keypad' },
  { id: 'pool_fence', name: 'Pool fence', icon: 'fence' },
  { id: 'safe', name: 'Safe', icon: 'safe' },
  { id: 'security_system', name: 'Security system', icon: 'security' },
  { id: 'smoke_detector', name: 'Smoke detector', icon: 'detector' },
];

// Standout category amenities
const standoutAmenities = [
  { id: 'beach_access', name: 'Beach access', icon: 'beach' },
  { id: 'car_access', name: 'Car access', icon: 'car' },
  { id: 'elevator', name: 'Elevator', icon: 'elevator' },
  {
    id: 'exercise_equipment',
    name: 'Exercise equipment',
    icon: 'exercise',
    hasOptions: true,
    options: ['Elliptical', 'Free weights', 'Stationary bike', 'Treadmill', 'Yoga mat', 'Rower', 'Other'],
  },
  { id: 'fenced_yard', name: 'Fenced yard', icon: 'fence' },
  { id: 'game_room', name: 'Game room', icon: 'gameroom' },
  { id: 'garden', name: 'Garden', icon: 'garden' },
  { id: 'gated_community', name: 'Gated community', icon: 'gate' },
  { id: 'home_gym', name: 'Home gym', icon: 'gym' },
  { id: 'home_theatre', name: 'Home theatre', icon: 'theatre' },
  { id: 'hot_tub', name: 'Hot tub', icon: 'hottub' },
  { id: 'housecleaning', name: 'Housecleaning (for additional fee)', icon: 'cleaning' },
  { id: 'ice_rink', name: 'Ice rink', icon: 'icerink' },
  { id: 'music_room', name: 'Music room', icon: 'music' },
  { id: 'ocean_view', name: 'Ocean view', icon: 'ocean' },
  { id: 'pets_allowed', name: 'Pets allowed', icon: 'pets' },
  { id: 'playroom', name: 'Playroom', icon: 'playroom' },
  { id: 'pool', name: 'Pool', icon: 'pool' },
  { id: 'sauna', name: 'Sauna', icon: 'sauna' },
  { id: 'sun_room', name: 'Sun room', icon: 'sunroom' },
];

/**
 * Category definitions with all amenities.
 * Categories are sorted alphabetically by name.
 */
export const amenityCategories = [
  {
    id: 'bathroom',
    name: 'Bathroom',
    amenities: bathroomAmenities,
  },
  {
    id: 'bedroom_and_laundry',
    name: 'Bedroom and laundry',
    amenities: bedroomAndLaundryAmenities,
  },
  {
    id: 'entertainment',
    name: 'Entertainment',
    amenities: entertainmentAmenities,
  },
  {
    id: 'family',
    name: 'Family',
    amenities: familyAmenities,
  },
  {
    id: 'heating_and_cooling',
    name: 'Heating and cooling',
    amenities: heatingAndCoolingAmenities,
  },
  {
    id: 'internet_and_office',
    name: 'Internet and office',
    amenities: internetAndOfficeAmenities,
  },
  {
    id: 'kitchen_and_dining',
    name: 'Kitchen and dining',
    amenities: kitchenAndDiningAmenities,
  },
  {
    id: 'outdoor',
    name: 'Outdoor',
    amenities: outdoorAmenities,
  },
  {
    id: 'standout',
    name: 'Standout',
    amenities: standoutAmenities,
  },
  {
    id: 'eco_friendly',
    name: 'Eco-friendly',
    amenities: ecoFriendlyAmenities,
  },
  {
    id: 'safety_and_property',
    name: 'Safety and property',
    amenities: safetyAndPropertyAmenities,
  },
  {
    id: 'accessibility',
    name: 'Accessibility',
    amenities: accessibilityAmenities,
  },
];

/**
 * Helper function to get all amenities across all categories
 */
export const getAllAmenities = () => {
  return amenityCategories.reduce((acc, category) => {
    return [...acc, ...category.amenities];
  }, []);
};

/**
 * Helper function to find an amenity by ID
 */
export const findAmenityById = amenityId => {
  const allAmenities = getAllAmenities();
  return allAmenities.find(amenity => amenity.id === amenityId);
};

/**
 * Helper function to get amenities with options
 */
export const getAmenitiesWithOptions = () => {
  return getAllAmenities().filter(amenity => amenity.hasOptions);
};

/**
 * Helper function to get amenities that require special input
 */
export const getAmenitiesWithRequiredInput = () => {
  return getAllAmenities().filter(amenity => amenity.requiresInput);
};
